"use client";

import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { Strings } from "@/components/tools/cagr-calculator";
import { MacroLineFigure } from "@/components/tools/econ-macro/line-figure";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  doublingYears,
  type GrowthMode,
  type GrowthSolution,
  growthPath,
  totalGrowth,
} from "@/lib/tool-kit/econ-macro/cagr";

const clean = (value: number) => (value === 0 ? 0 : value);

export function CagrResults({
  solution,
  mode,
  strings,
}: {
  solution: GrowthSolution;
  mode: GrowthMode;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const { begin, end, rate, years } = solution;
  const num = (value: number) =>
    fmt(clean(value), { maximumFractionDigits: 4 });
  const pct = (value: number) =>
    `${fmt(clean(value) * 100, { maximumFractionDigits: 3 })}%`;
  const total = totalGrowth(begin, end);
  const doubling = doublingYears(rate);
  const path = growthPath(begin, rate, years);
  const values = {
    begin: num(begin),
    end: num(end),
    rate: pct(rate),
    rateDecimal: num(rate),
    years: num(years),
    total: pct(total),
    simple: pct(total / years),
    doubling: doubling === null ? strings.noDoubling : num(doubling),
  };
  const working = {
    rate: strings.workCagr,
    future: strings.workFuture,
    years: strings.workYears,
  }[mode];
  const headline = {
    rate: { label: strings.cagrCard, value: pct(rate) },
    future: { label: strings.endingCard, value: num(end) },
    years: { label: strings.yearsCard, value: num(years) },
  }[mode];
  const line = "font-mono text-muted-foreground text-sm leading-relaxed";
  return (
    <div className="space-y-5">
      <StatGrid>
        <StatCard emphasis label={headline.label} value={headline.value} />
        {mode === "rate" ? null : (
          <StatCard label={strings.cagrCard} value={pct(rate)} />
        )}
        <StatCard label={strings.totalGrowthCard} value={pct(total)} />
        <StatCard label={strings.multipleCard} value={`${num(end / begin)}×`} />
        <StatCard label={strings.gainCard} value={num(end - begin)} />
        <StatCard label={strings.simpleCard} value={pct(total / years)} />
        <StatCard label={strings.doublingCard} value={values.doubling} />
      </StatGrid>
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className={line}>{t(working, values)}</p>
        <p className={line}>{t(strings.workTotal, values)}</p>
        <p className={line}>{t(strings.workSimple, values)}</p>
        {doubling === null ? null : (
          <p className={line}>{t(strings.workDoubling, values)}</p>
        )}
      </div>
      <h3 className={WIDGET_H3}>{strings.pathHeading}</h3>
      <DataTable
        head={[strings.yearColumn, strings.valueColumn, strings.growthColumn]}
        rows={path.map((point) => [
          num(point.year),
          num(point.value),
          pct(totalGrowth(begin, point.value)),
        ])}
      />
      <MacroLineFigure
        description={t(strings.chartDescription, values)}
        fallbackName="cagr"
        fmt={fmt}
        id="cagr-chart"
        labels={strings}
        names={[strings.seriesValue]}
        numeric
        rows={path.map((point) => ({
          label: num(point.year),
          x: point.year,
          values: [point.value],
        }))}
        showTable={false}
        title={strings.chartTitle}
        xTitle={strings.chartXTitle}
        yTitle={strings.chartYTitle}
      />
      <Notice>{strings.smoothNote}</Notice>
    </div>
  );
}
